import React, { useEffect, useMemo, useRef } from 'react';
import { createFilterOptions, Autocomplete, Checkbox, Chip, TextField, Tooltip } from '@mui/material';
import { useSelectUsers } from '../../Schedule.queries';
import { resolveId } from '../../../../../../../utils';
import useIsMobile from '../../../../../../hooks/useIsMobile';

const getOptionLabel = (option) => (option.category === 'Users' ? `${option.name} - ${option.license_number ?? option.hauler_number ?? 'unknown'}` : option.name);

const SelectUsers = ({ queryParams, userGroupsEnabled = true, label, selectAll = true, showAll = true, value, onChange, options, queryOptions, initialSelectedIds, onInitialSelect, ...rest }) => {
    const isMobile = useIsMobile();
    const [
        { data: userGroups, userGroupsLoading, userGroupsError },
        { data: users, usersLoading, usersError },
        // wrap
    ] = useSelectUsers({ queryParams, userGroupsEnabled, ...queryOptions });
    const loading = usersLoading ?? userGroupsLoading;
    const error = usersError ?? userGroupsError;

    const data = useMemo(() => {
        const roleGroups = userGroups
            ?.map((group) => {
                const user_ids = [];
                group.users.forEach((user) => {
                    const resolvedUser = resolveId(user);
                    if (user.role === (queryParams.role ?? user.role) && resolvedUser.id) user_ids.push(resolvedUser.id);
                });
                const newGroup = { name: group.name, id: group._id, userIds: user_ids, category: 'User Groups' };
                return user_ids.length ? newGroup : undefined;
            })
            .filter(Boolean);

        const newData = [];
        if (selectAll) newData.push({ name: 'Select All', id: 'select_all' });
        if (roleGroups) newData.push(...roleGroups);
        if (users) newData.push(...users.toSorted((a, b) => a.name.localeCompare(b.name)).map((user) => ({ ...resolveId(user), category: 'Users' })));

        return newData;
    }, [selectAll, userGroups, users]);

    const availableOptions = useMemo(() => {
        if (!options || showAll) return data;
        const filteredOptions = data.filter(({ id }) => options.includes(id));
        return filteredOptions ?? data;
    }, [data, options, showAll]);

    const resolvedInitialValue = useMemo(() => {
        if (!initialSelectedIds?.length || !data?.length) return null;
        const matched = data.filter((item) => item.category === 'Users' && initialSelectedIds.includes(item.id));
        return matched.length > 0 ? matched : null;
    }, [data, initialSelectedIds]);

    // Sync pre-selected values to form state for submit
    const hasSynced = useRef(false);
    useEffect(() => {
        if (hasSynced.current || !resolvedInitialValue) return;
        hasSynced.current = true;
        (onInitialSelect ?? onChange)(resolvedInitialValue);
    }, [resolvedInitialValue]);

    // Show resolved initial value until form state catches up or user interacts
    const userInteracted = useRef(false);
    const displayValue = useMemo(() => {
        if (userInteracted.current || !resolvedInitialValue) return value;
        return value?.length > 0 ? value : resolvedInitialValue;
    }, [value, resolvedInitialValue]);

    const handleUserChange = ({ newValue, reason, option }) => {
        userInteracted.current = true;
        // If value is select all, set all users
        if (option?.id === 'select_all') {
            if (reason === 'selectOption') onChange(data);
            if (reason === 'removeOption') onChange([]);
            return;
        }

        // If removing a user group, remove all users in the group
        if (option?.category === 'User Groups' && reason === 'removeOption') {
            const usersFromGroups = option.userIds;
            const newUsers = newValue.filter((user) => !usersFromGroups.includes(user));
            onChange(newUsers);
            return;
        }

        // If adding a user group, make sure to include all users in the group
        if (option?.category === 'User Groups' && reason === 'selectOption') {
            const usersFromGroups = newValue
                .filter((v) => v.category === 'User Groups')
                .map((group) => group.userIds)
                .flat();
            const alreadySelected = newValue.filter((v) => v.category === 'Users').map((user) => user.id);
            const newUserIds = usersFromGroups.filter((id) => !alreadySelected.includes(id));
            const newUsers = data.filter((user) => newUserIds.includes(user.id));

            onChange([...newValue, ...newUsers].filter((user) => user.id !== 'select_all'));
            return;
        }

        onChange(newValue);
    };

    if (error) return <div>Error: {error.message}</div>;
    if (!data?.length) return <div>No data</div>;

    const filterOptions = createFilterOptions({
        stringify: (option) => getOptionLabel(option),
        trim: true,
    });

    return (
        <Autocomplete
            size="small"
            fullWidth
            loading={loading}
            value={displayValue}
            onChange={(_e, newValue, reason, details) => handleUserChange({ newValue, reason, option: details?.option })}
            options={availableOptions}
            groupBy={(option) => option.category}
            filterOptions={filterOptions}
            getOptionLabel={(option) => option.id ?? option._id}
            renderOption={({ id, ...optionProps }, option, { selected }) => (
                <li key={id} {...optionProps}>
                    <Checkbox size="small" sx={{ p: 0, pr: 1 }} checked={selected} />
                    {getOptionLabel(option)}
                </li>
            )}
            renderTags={(val, getTagProps) => {
                const tagValues = [];
                // Remove select all and user groups, find the associated option/name to display
                val.forEach((v, index) => {
                    const option = data.find((d) => (d.id ?? d._id) === (v.id ?? v._id ?? v));
                    if (option?.id !== 'select_all' && option?.category !== 'User Groups') {
                        tagValues.push({ ...option, originalIndex: index });
                    }
                });
                const displayTag = tagValues[0];
                const remaining = tagValues.length - 1;
                if (!displayTag) return null;
                return (
                    <>
                        <Tooltip title={getOptionLabel(displayTag)}>
                            <Chip label={displayTag.name} {...getTagProps({ index: displayTag.originalIndex })} />
                        </Tooltip>
                        {remaining > 0 && (
                            <Tooltip
                                title={
                                    <div style={{ whiteSpace: 'pre-line' }}>
                                        {tagValues
                                            .splice(1)
                                            .map((tag) => getOptionLabel(tag))
                                            .join('\n')}
                                    </div>
                                }
                            >
                                <Chip label={`+${remaining}`} />
                            </Tooltip>
                        )}
                    </>
                );
            }}
            isOptionEqualToValue={(option, val) => (option.id ?? option._id) === (val.id ?? val._id ?? val)}
            multiple
            disableCloseOnSelect
            renderInput={(params) => <TextField {...params} label={label} />}
            limitTags={1}
            getLimitTagsText={(more) => <Chip label={`+${more}`} />}
            sx={{
                '.MuiAutocomplete-inputRoot': {
                    display: 'flex',
                    flexWrap: 'nowrap',
                    height: isMobile ? 'auto' : '40px',
                },
                '.MuiChip-root': {
                    height: '24px',
                    maxWidth: '70%',
                },
            }}
            {...rest}
        />
    );
};

export default SelectUsers;
